import { AppServices } from '../../contexts/AppServicesContext';
import { createDomainContext, DomainContext } from '../changes/DomainContext';
import { PendingChangesStore } from '../changes/PendingChangesStore';
import { Patient } from '../patient/patient';
import { completeHydration } from '../patient/hydration';
import type { ConnectorPatientManifest } from '../persistence/ConnectorPatientManifest';
import { ConnectorImageGateway } from '../adapters/ConnectorImageGateway';
import { hydratePersistenceRefs } from '../persistence/hydratePersistenceRefs';
import { PersistenceRegistry } from '../persistence/PersistenceRegistry';
import {
    ConnectorDatasetDeletionGateway,
    ConnectorRegistrationGateway,
} from '../adapters/ConnectorRegistrationGateway';
import { ConnectorStructureSetGateway } from '../adapters/ConnectorStructureSetGateway';
import { ContourEditBridge } from '../adapters/ContourEditBridge';
import { EngineAdapter } from '../adapters/EngineAdapter';
import { RegistrationEditBridge } from '../adapters/RegistrationEditBridge';
import { DoseSaveService } from '../services/dose/DoseSaveService';
import { ImageSaveService } from '../services/image/ImageSaveService';
import { RegistrationSaveService } from '../services/registration/RegistrationSaveService';
import { PatientSaveService } from '../services/save/PatientSaveService';
import { makeSaveEffectApplier } from '../services/save/SaveEffect';
import { StructureSetSaveService } from '../services/structureSet/StructureSetSaveService';
import {
    LoggingPersistenceService,
    PersistenceService,
} from '../adapters/LoggingPersistenceService';
import { DoseService } from '../services/dose/DoseService';
import { ImageService } from '../services/image/ImageService';
import { RegistrationService } from '../services/registration/RegistrationService';
import { ContourService } from '../services/structureSet/ContourService';
import { RoiService } from '../services/structureSet/RoiService';
import { StructureSetExportService } from '../services/structureSet/StructureSetExportService';
import { StructureSetService } from '../services/structureSet/StructureSetService';
import { DisplayPreferencesStore } from './DisplayPreferencesStore';
import { domainTrace } from '../diagnostics/domainTrace';
import { getContourUndoService } from '../ServiceContainer';
// RecoilBridge wired in later tasks

export interface PatientSession {
    readonly context: DomainContext;
    readonly patient: Patient;
    readonly structureSetService: StructureSetService;
    readonly structureSetExportService: StructureSetExportService;
    readonly roiService: RoiService;
    readonly registrationService: RegistrationService;
    readonly doseService: DoseService;
    readonly imageService: ImageService;
    readonly contourService: ContourService;
    readonly displayPreferences: DisplayPreferencesStore;
    readonly engineAdapter: EngineAdapter;
    readonly persistenceService: PersistenceService;
    /**
     * Connector-next identity of the loaded aggregate. Empty (null
     * patientRef) when the session has no connector backing — database
     * saving is then unavailable.
     */
    readonly persistenceRegistry: PersistenceRegistry;
    /** Accumulated per-object dirty state for the save workflow. */
    readonly pendingChangesStore: PendingChangesStore;
    /** Persists pending dataset changes to the connector database. */
    readonly patientSaveService: PatientSaveService;
    dispose(): void;
}

/**
 * @param currentUserName Read at each save to name the user to the
 * connector, which records it only where it cannot establish the caller
 * itself.
 */
export function createPatientSession(
    appServices: AppServices,
    patient: Patient,
    connectorManifest: ConnectorPatientManifest | null = null,
    currentUserName: () => string | undefined = () => undefined
): PatientSession {
    completeHydration(patient);
    const persistenceRegistry = hydratePersistenceRefs(
        patient,
        connectorManifest
    );
    const context = createDomainContext();
    patient.attachTracker(context.tracker);
    domainTrace.session('create', { patientId: patient.id });

    const structureSetService = new StructureSetService(context, patient);
    const structureSetExportService = new StructureSetExportService(patient);
    const roiService = new RoiService(context, patient);
    const registrationService = new RegistrationService(context, patient);
    const doseService = new DoseService(context, patient);
    const imageService = new ImageService(context, patient);
    const contourService = new ContourService(context, patient);
    const displayPreferences = new DisplayPreferencesStore();
    const engineAdapter = new EngineAdapter(appServices.engine, context.store);
    const persistenceService = new LoggingPersistenceService(context.store);
    const pendingChangesStore = new PendingChangesStore(
        context.store,
        persistenceRegistry,
        patient
    );
    const contourEditBridge = new ContourEditBridge(
        patient,
        contourService,
        getContourUndoService()
    );
    const registrationEditBridge = new RegistrationEditBridge(
        registrationService
    );
    // Savers are pure I/O: they hold a read-only registry view and no
    // aggregate, and every session mutation their calls earn comes back
    // as a SaveEffect. This applier — with the live patient and
    // tracker in closure — is the one place those effects land.
    const applySaveEffect = makeSaveEffectApplier(persistenceRegistry, {
        adoptRegistrationFileIdentity: (id, identity) => {
            patient.registrations
                .find((reg) => reg.id === id)
                ?.adoptPersistedFileIdentity(identity);
        },
        adoptStructureSetOrigin: (id, origin) => {
            patient.getStructureSetById(id)?.adoptRecordedOrigin(origin);
        },
        adoptStructureSetReviews: (id, reviews) => {
            patient.getStructureSetById(id)?.adoptRecordedRoiReviews(reviews);
        },
        markStructureSetUploaded: (id, sopInstanceUid) => {
            const ss = patient.getStructureSetById(id);
            if (ss) {
                context.tracker.batch(() => {
                    ss.setSyncStatus('uploaded');
                });
                if (sopInstanceUid) {
                    ss.adoptStoredSopInstanceUid(sopInstanceUid);
                }
            }
        },
    });
    const structureSetSaveService = new StructureSetSaveService(
        persistenceRegistry,
        new ConnectorStructureSetGateway(currentUserName)
    );
    const registrationSaveService = new RegistrationSaveService(
        persistenceRegistry,
        new ConnectorRegistrationGateway()
    );
    const datasetDeletionGateway = new ConnectorDatasetDeletionGateway();
    const imageSaveService = new ImageSaveService(
        persistenceRegistry,
        new ConnectorImageGateway(),
        datasetDeletionGateway
    );
    const doseSaveService = new DoseSaveService(
        persistenceRegistry,
        datasetDeletionGateway
    );
    const patientSaveService = new PatientSaveService(
        patient,
        persistenceRegistry,
        pendingChangesStore,
        structureSetSaveService,
        imageSaveService,
        registrationSaveService,
        doseSaveService,
        applySaveEffect
    );

    const disposers: Array<() => void> = [
        () => engineAdapter.dispose(),
        () => persistenceService.dispose(),
        () => pendingChangesStore.dispose(),
        () => contourEditBridge.dispose(),
        () => registrationEditBridge.dispose(),
    ];
    let disposed = false;

    return {
        context,
        patient,
        structureSetService,
        structureSetExportService,
        roiService,
        registrationService,
        doseService,
        imageService,
        contourService,
        displayPreferences,
        engineAdapter,
        persistenceService,
        persistenceRegistry,
        pendingChangesStore,
        patientSaveService,
        // Idempotent: useUnloadPatient disposes synchronously, then
        // PatientSessionProvider's effect cleanup runs on the next
        // render and would otherwise dispose a second time.
        dispose() {
            if (disposed) {
                return;
            }
            disposed = true;
            domainTrace.session('dispose', { patientId: patient.id });
            for (let i = disposers.length - 1; i >= 0; i--) {
                disposers[i]();
            }
        },
    };
}
